// Who the app is drawn for (E12-S1-T1). The answer comes from GET /auth/me through the API client and
// TanStack Query; the one thing kept outside the query is the signed-out flip, because any request
// can cause it: a 401 from anywhere calls the client's onUnauthorised, which records the path the
// user was on and holds the app signed-out from then on. The address bar is left as it is, and the
// kept path is the return for sign-in (src/auth/sign-in.tsx). Once signed out, the app stops asking:
// the session check is the only request a signed-out app makes.
//
// The store also owns the one top-level navigation the app makes on its own, so the tests can watch
// it: leaving for the identity provider's end-session address after sign-out (src/auth/session.ts).
//
// Nothing about the session is stored in the browser: no token, no web storage. The cookie is the API's.
import type { Principal, Role } from "@waypoint/contracts";
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";
import { useApi } from "../api/provider.js";

export type SessionState =
  | { readonly status: "checking" }
  | { readonly status: "signed-in"; readonly principal: Principal }
  | { readonly status: "signed-out"; readonly returnTo: string }
  | { readonly status: "failed"; readonly error: unknown };

/** The signed-out flip, set by a 401 and read by the shell. */
export interface SessionStore {
  /** The path to return to after signing in, once a 401 has been seen; undefined until then. */
  readonly signedOutAt: () => string | undefined;
  readonly signOut: (returnTo: string) => void;
  readonly subscribe: (listener: () => void) => () => void;
  /** A top-level navigation away from the app (sign-out's end-session address). Never a fetch. */
  readonly leave: (address: string) => void;
}

export function createSessionStore(
  leave: (address: string) => void = (address) => {
    window.location.assign(address);
  },
): SessionStore {
  let returnTo: string | undefined;
  const listeners = new Set<() => void>();
  return {
    leave,
    signedOutAt: () => returnTo,
    signOut: (path) => {
      if (returnTo !== undefined) return; // the first 401 names the page the user was on
      returnTo = path;
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** The path and query the user is on: the return after sign-in. Never the fragment. */
export function currentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

export const meQueryKey = ["auth", "me"] as const;

const SessionContext = createContext<SessionStore | null>(null);

export function SessionProvider({ store, children }: { store: SessionStore; children: ReactNode }) {
  return <SessionContext value={store}>{children}</SessionContext>;
}

export function useSessionStore(): SessionStore {
  const store = useContext(SessionContext);
  if (store === null) throw new Error("useSessionState() outside <SessionProvider>");
  return store;
}

export function useSessionState(): SessionState {
  const store = useSessionStore();
  const api = useApi();
  const returnTo = useSyncExternalStore(store.subscribe, store.signedOutAt);
  const me = useQuery({
    queryKey: meQueryKey,
    queryFn: () => api.me(),
    enabled: returnTo === undefined,
  });
  if (returnTo !== undefined) return { status: "signed-out", returnTo };
  if (me.isPending) return { status: "checking" };
  if (me.isError) return { status: "failed", error: me.error };
  return { status: "signed-in", principal: me.data };
}

export function isTenantAdmin(principal: Principal): boolean {
  return principal.roles.includes("tenant-admin");
}

/** The roles in the order the account menu names them: the broadest first. */
const ROLE_ORDER: readonly Role[] = [
  "platform-admin",
  "tenant-admin",
  "content-owner",
  "manager",
  "associate",
  "client-reviewer", // ADR-031: answers and waives source issues, nothing else
];

export function rolesInOrder(principal: Principal): Role[] {
  return ROLE_ORDER.filter((role) => principal.roles.includes(role));
}
